// @flow
import React, { Component } from 'react';
import isNil from 'lodash/fp/isNil';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import type { Bloc, BlocContent } from 'types/NewsletterBloc';
import 'components/fragments/SimpleCarousel/SimpleCarousel.css';
import BackgroundImage from 'containers/fragments/BackgroundImageContainer';
import { dynamicClassName } from 'utils/dynamicClassName';

type Props = {
  onCloseRequest: Function,
  data: Array<Bloc|BlocContent>,
  initialIndex: number,
};

type State = {
  focusImg: number,
};

class SimpleCarousel extends Component<Props, State> {
  modal: ?HTMLElement;

  static defaultProps = {
    initialIndex: 0,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      focusImg: props.initialIndex,
    };
  }

  componentDidMount() {
    window.addEventListener('keyup', this.handleKeyUp, false);
    document.addEventListener('click', this.handleOutsideClick, false);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    /**
     * On a changé de média et donc potentiellement de video, on reload la video
     */
    if (prevState.focusImg !== this.state.focusImg) {
      const video = document.getElementsByTagName('video')[0];
      if (video) {
        video.load();
      }
    }
  }
  componentWillUnmount() {
    window.removeEventListener('keyup', this.handleKeyUp, false);
    document.removeEventListener('click', this.handleOutsideClick, false);
  }

  handleKeyUp = (e: KeyboardEvent) => {
    const { onCloseRequest } = this.props;
    const keys = {
      '27': () => {
        e.preventDefault();
        onCloseRequest();
        window.removeEventListener('keyup', this.handleKeyUp, false);
      },
      '37': () => {
        e.preventDefault();
        this._handleClickLeft();
      },
      '39': () => {
        e.preventDefault();
        this._handleClickRight();
      }
    };

    if (keys[e.keyCode]) {
      keys[e.keyCode]();
    }
  };

  handleOutsideClick = (e: Event) => {
    const { onCloseRequest } = this.props;

    if (!isNil(this.modal) && this.modal) {
      if (e.target instanceof HTMLInputElement) {
        if (!this.modal.contains(e.target)) {
          onCloseRequest();
          document.removeEventListener('click', this.handleOutsideClick, false);
        }
      }
    }
  };

  _renderChildrens = () => {
    const { data } = this.props;
    const { focusImg } = this.state;
    let nextImage, prevImage;
    const prev = focusImg - 1;
    const next = focusImg + 1;
    if (data[prev] && data[prev].featured_image) {
      prevImage = <img alt="Précédente" className="prevImage" src={data[prev].featured_image.src}/>;
    }
    if (data[next] && data[next].featured_image) {
      nextImage = <img alt="Suivante" className="nextImage" src={data[next].featured_image.src}/>;
    }

    if (Object.prototype.hasOwnProperty.call(data[focusImg], 'iframe')) {
      const {
        children
      } = data[focusImg];
      return (
        <>
          {prevImage}
          <div className="wp-block-embed-container">
            { children && children.length > 0 && 
              children.map((blocFigure, index) => <ContentBloc key={index} {...blocFigure} />) }
          </div>
          {nextImage}
        </>
      );
    }
    if (Object.prototype.hasOwnProperty.call(data[focusImg], 'featured_video')) {
      const {
        featured_video: {
          src,
        } = {
          src: ''
        },
      } = data[focusImg]; 

      return (
        <>
          {prevImage}
          <figure className="image-caption-fig">
            <div>
              <video style={{ height: 'inherit' }} controls>
                <source src={src} />
              </video>
              {data[focusImg].figcaption && 
              <figcaption className="caption-image">{data[focusImg].figcaption}</figcaption>}
            </div>
          </figure>
          {nextImage}
        </>
      );
    }
    if (data[focusImg].featured_image) {
      return (
        <>
          {prevImage}
          <figure className="image-caption-fig">
            <div>
              <img alt="" src={data[focusImg].featured_image.src}/>
              {data[focusImg].figcaption &&
              <figcaption className="caption-image">{data[focusImg].figcaption}</figcaption>}
            </div>
          </figure>
          {nextImage}
        </>
      );
    }
    return null;
  };

  _handleClickRight = () => {
    const { data } = this.props;
    const { focusImg } = this.state;

    if (data.length - 1 <= focusImg) {
      this.setState({
        focusImg: 0,
      });
    } else {
      this.setState((prevState) => ({
        focusImg: prevState.focusImg + 1,
      }));
    }
  };

  _handleClickLeft = () => {
    const { data } = this.props;
    const { focusImg } = this.state;

    if (focusImg <= 0) {
      this.setState({
        focusImg: data.length - 1,
      });
    } else {
      this.setState((prevState) => ({
        focusImg: prevState.focusImg - 1,
      }));
    }
  };

  handleClick = (id: number) => {
    return () => {
      this.setState({
        focusImg: id,
      });
    };
  };

  renderListImg = () => {
    const { data } = this.props;
    const { focusImg } = this.state;

    return data.map<any>((blocFigure: any, index) => {
      const {
        tagName,
        iframe,
      } = blocFigure;
      if (blocFigure.featured_image) {
        const pictureClassName = dynamicClassName("picture");
        let isVideo = false;
        if (focusImg === index) {
          pictureClassName.add("picture--active");
        }
        if (tagName === 'video' || iframe) {
          pictureClassName.add("box card card--video");
          isVideo = true;
        }

        return (
          <div
            role='button'
            tabIndex={0}
            className={pictureClassName.build()}
            key={index}
            onClick={this.handleClick(index)}>
            <BackgroundImage
              className="picture__thumbnail"
              illustration={blocFigure.featured_image.src}
              srcset={blocFigure.featured_image.srcset} />
            {isVideo && 
              <div className="card__footer" style={{ zIndex: 1 }}>
                <i className="icon icon-play" />
              </div>
            }
          </div>
        );
      }
      return null;
    });
  };

  render() {
    const {
      onCloseRequest,
    } = this.props;
    return (
      <div className='modalOverlay'
        ref={r => (this.modal = r)}>
        <div className='modalTop'>
          <i
            role="button"
            tabIndex={0}
            style={{ display: 'block' }}
            className='icon icon-close_alt menu__close js-showMenu'
            onClick={onCloseRequest} />
        </div>
        <div className="modalWrapper">
          <div className='leftButtonContainer'>
            <button
              type='button'
              className='leftButton'
              onClick={this._handleClickLeft}>
              <i className="icon icon-chevron-left"></i>
            </button>
          </div>
          <div className='modalContent'>
            {this._renderChildrens()}
          </div>
          <div className='rightButtonContainer'>
            <button
              type='button'
              className='rightButton'
              onClick={this._handleClickRight}>
              <i className="icon icon-chevron-right"></i>
            </button>
          </div>
        </div>
        <div className="modalFooter">
          <div className="news-slider">
            <div className="news-slider__wrapper--picture">
              {this.renderListImg()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default SimpleCarousel;
